import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { campaignSlugs, isOfferExpired } from '@zapier/promotions';

import { PlanType } from '@zapier/design-system-revenue/src/types';

import { reducedTeamCompanyOfferBanner_pendingOfferQuery as PendingOfferQuery } from './types/reducedTeamCompanyOfferBanner_pendingOfferQuery';
import { reducedTeamCompanyOfferBanner_offerDetailsQuery as OfferDetailsQuery } from './types/reducedTeamCompanyOfferBanner_offerDetailsQuery';

export const currentUserQuery = gql`
  query reducedTeamCompanyOfferBanner_currentUserQuery {
    currentUserV2 {
      id
    }
  }
`;

export const pendingOfferQuery = gql`
  query reducedTeamCompanyOfferBanner_pendingOfferQuery($accountId: Int!) {
    account(id: $accountId) {
      id
      pendingOfferId
    }
  }
`;

export const offerDetailsQuery = gql`
  query reducedTeamCompanyOfferBanner_offerDetailsQuery(
    $accountId: Int!
    $offerId: ID!
  ) {
    account(id: $accountId) {
      id
      offer(offerId: $offerId) {
        id
        acceptedAt
        expiry
        variant {
          id
          campaign {
            id
            slug
          }
          isControl
        }
      }
      plan: planV2 {
        id
        type
      }
    }
  }
`;

type Props = {
  skip?: boolean;
};

export const useData = ({ skip }: Props = {}) => {
  const accountId = localStorage.getItem('currentAccountId');

  // Grab the user's pendingOfferId.
  const {
    data: pendingOfferData,
    loading: isLoadingPendingOffer,
    error: pendingOfferError,
  } = useQuery<PendingOfferQuery>(pendingOfferQuery, {
    skip,
    variables: {
      accountId: accountId ? parseInt(accountId) : undefined,
    },
  });

  const pendingOfferId = pendingOfferData?.account?.pendingOfferId;

  // Grab details for the pending offer.
  const {
    data: offerDetailsData,
    loading: isLoadingOfferDetails,
    error: offerDetailsError,
  } = useQuery<OfferDetailsQuery>(offerDetailsQuery, {
    skip: skip || isLoadingPendingOffer || !pendingOfferId,
    variables: {
      accountId: accountId ? parseInt(accountId) : undefined,
      offerId: pendingOfferId,
    },
  });

  const offer = offerDetailsData?.account?.offer;
  const campaignSlug = offer?.variant?.campaign?.slug ?? '';

  const planType = (campaignSlug === campaignSlugs.TEAM_REDUCED_TASK
    ? 'Team'
    : 'Company') as PlanType;

  const isTeamPromoAndUserOnTeamOrCompanyPlan =
    planType === 'Team' &&
    ['Team', 'Company'].includes(offerDetailsData?.account?.plan?.type ?? '');

  const isCompanyPromoAndUserOnCompanyPlan =
    planType === 'Company' &&
    offerDetailsData?.account?.plan?.type === 'Company';

  return {
    discount: planType === 'Team' ? 10000 : 20000,
    error:
      pendingOfferError || offerDetailsError
        ? 'There was an error fetching offer information. Please try again.'
        : null,
    isLoading: isLoadingPendingOffer || isLoadingOfferDetails,
    pendingOfferId,
    planType,
    shouldRender:
      !isOfferExpired(offer?.expiry) &&
      !offer?.acceptedAt &&
      [
        campaignSlugs.TEAM_REDUCED_TASK,
        campaignSlugs.COMPANY_REDUCED_TASK,
      ].includes(campaignSlug) && // offer is either reduced Team or reduced Company promo
      !offer?.variant?.isControl &&
      !isTeamPromoAndUserOnTeamOrCompanyPlan &&
      !isCompanyPromoAndUserOnCompanyPlan,
  };
};
